import { Globe } from 'lucide-react';
import { Building2, GraduationCap, MapPin, AlertTriangle, CheckCircle2, BarChart3, Activity, Clock } from 'lucide-react';
import { useDashboardApi, KPICard, LoadingSpinner, ErrorPanel, EntityStatusList, AlertsList, FeedTable, StatsPanel, ResponsiveStyles } from '../../components/dashboard/DashboardShared';
import '../../components/ui/datatable.css';

import { useAuthStore } from '../../store/authStore';

export function DashboardRede() {
  const { user, isLoading: authLoading } = useAuthStore();
  const redeId = user?.redeId || (user as any)?.rede_id || (user as any)?.redeEnsinoId;

  const { data, isLoading, error } = useDashboardApi(
    `dashboard-rede-${redeId || 'none'}`,
    redeId ? `/redes/${redeId}/dashboard` : '',
  );

  if (authLoading) return <LoadingSpinner label="Identificando rede..." />;

  if (!redeId) {
    return (
      <div className="admin-page animate-fadeIn p-6">
        <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-2xl p-8 text-center max-w-xl mx-auto mt-12 shadow-sm">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Rede de Ensino Não Vinculada</h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 leading-relaxed">
            Seu usuário possui o perfil de <strong>GESTOR DE REDE</strong>, mas ainda não está associado a nenhuma Rede cadastrada.
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-4">
            Contate o Administrador do Sistema para vincular sua conta à respectiva Rede Municipal ou Estadual.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) return <LoadingSpinner label="Carregando Dashboard da Rede..." />;
  if (error) return <ErrorPanel />;

  const c = data?.cards || {};
  const chamadas = data?.operacao?.chamadasDoDia || {};
  const notif = data?.analitico?.resumoNotificacoes || {};

  return (
    <div className="admin-page animate-fadeIn">
      <div className="page-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Globe size={28} style={{ color: '#7c3aed' }} />
            {data?.rede?.nome || 'Dashboard da Rede'}
          </h1>
          <p>Visão consolidada da rede — {new Date().toLocaleDateString('pt-BR')}</p>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-4">
        <KPICard icon={MapPin} label="Estados" value={c.totalEstados || 0} sub={`${c.totalEstadosComProblema || 0} com alertas`} color="#7c3aed" />
        <KPICard icon={MapPin} label="Municípios" value={c.totalMunicipios || 0} color="#2563eb" />
        <KPICard icon={Building2} label="Escolas" value={c.totalEscolas || 0} color="#059669" />
        <KPICard icon={GraduationCap} label="Alunos" value={c.totalAlunosPrevistos?.toLocaleString('pt-BR') || 0} color="#6366f1" />
      </div>

      <div className="kpi-grid kpi-grid-4" style={{ marginTop: 16 }}>
        <KPICard icon={CheckCircle2} label="Presentes" value={c.totalPresentes?.toLocaleString('pt-BR') || 0} sub={`${c.totalAtrasados || 0} atrasados`} color="#16a34a" />
        <KPICard icon={AlertTriangle} label="Ausentes" value={c.totalAusentes?.toLocaleString('pt-BR') || 0} color="#dc2626" />
        <KPICard icon={AlertTriangle} label="Inconsistências" value={c.totalInconsistencias || 0} color="#d97706" />
        <KPICard icon={AlertTriangle} label="Alertas Ativos" value={c.totalAlertasAtivos || 0} color="#ef4444" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 24 }}>
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChart3 size={18} style={{ color: '#7c3aed' }} /> Status por Estado
          </h3>
          <EntityStatusList items={data?.operacao?.statusEstados || []} nameKey="estado" subLabel="municípios" />
        </div>

        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} style={{ color: '#dc2626' }} /> Alertas Críticos
          </h3>
          <AlertsList alertas={data?.alertas?.criticos || []} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 24 }}>
        <StatsPanel title="Notificações Hoje" icon={Clock} iconColor="#6366f1" items={[
          { label: 'Total Disparadas', value: notif.total || 0 },
          { label: 'Entregues', value: notif.entregues || notif.enviadas || 0, color: '#2563eb' },
          { label: 'Lidas', value: notif.lidas || 0, color: '#16a34a' },
          { label: 'Falhas', value: notif.falhas || 0, color: '#dc2626' },
          { label: 'Taxa Leitura', value: `${notif.taxaLeitura || 0}%`, color: '#7c3aed' },
        ]} />
        <StatsPanel title="Chamadas Hoje" icon={CheckCircle2} iconColor="#059669" items={[
          { label: 'Total', value: chamadas.total || 0 },
          { label: 'Abertas', value: chamadas.abertas || 0, color: '#d97706' },
          { label: 'Concluídas', value: chamadas.concluidas || 0, color: '#16a34a' },
          { label: 'Canceladas', value: chamadas.canceladas || 0, color: '#6b7280' },
        ]} />
      </div>

      <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20, marginTop: 24 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={18} style={{ color: '#2563eb' }} /> Feed — Últimos Eventos
        </h3>
        <FeedTable feed={data?.operacao?.feed || []} />
      </div>

      <ResponsiveStyles />
    </div>
  );
}
